/**
 * Diálogo "Registrar servicio" de un mantenimiento.
 *
 * Propone el servicio que está en ventana (le toca); si no hay, el omitido
 * más antiguo. Un omitido se puede registrar tarde: queda como "Hecho tarde"
 * y no borra la decisión del gerente. "Servicio extra" es uno fuera del plan.
 * El km es informativo.
 */
import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { FP, fpInputSx, fpMultilineInputSx } from "components/financepro";
import { fmtFecha, aFecha, ESTADOS_SERVICIO } from "./calendario";
import { btnSolid, btnBase, fmtPrecio } from "./comunes";

const EXTRA = "extra";

function sugerido(pendientes) {
  const letoca = pendientes.find((s) => s.estado === "letoca");
  if (letoca) return String(letoca.numero);
  const omitido = pendientes.find((s) => s.estado === "omitido");
  if (omitido) return String(omitido.numero);
  return pendientes.length ? String(pendientes[0].numero) : EXTRA;
}

export default function RegistrarServicio({
  abierto,
  garantia,
  calendario,
  hoy,
  guardando = false,
  onCerrar,
  onRegistrar,
}) {
  const pendientes = useMemo(
    () => (calendario || []).filter((s) => !["hecho", "tarde"].includes(s.estado)),
    [calendario]
  );

  const [numero, setNumero] = useState(EXTRA);
  const [fecha, setFecha] = useState(hoy);
  const [km, setKm] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [costo, setCosto] = useState("0");
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!abierto) return;
    const n = sugerido(pendientes);
    setNumero(n);
    setFecha(hoy);
    setKm("");
    setTecnico("");
    setObservaciones("");
    setError("");
    const plan = pendientes.find((s) => String(s.numero) === n);
    setCosto(String(plan ? plan.precio : 0));
  }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarServicio = (n) => {
    setNumero(n);
    const plan = pendientes.find((s) => String(s.numero) === n);
    setCosto(String(plan ? plan.precio : 0));
    setError("");
  };

  const guardar = () => {
    if (!fecha) return setError("Indique la fecha del servicio");
    if (aFecha(fecha) > aFecha(hoy)) return setError("La fecha no puede ser futura");
    if (aFecha(fecha) < aFecha(garantia.fechaVenta))
      return setError("La fecha no puede ser anterior a la venta");
    const kmNum = km === "" ? null : Number(km);
    if (kmNum !== null && (!Number.isInteger(kmNum) || kmNum < 0))
      return setError("El km debe ser un número entero");
    const costoNum = Number(costo);
    if (Number.isNaN(costoNum) || costoNum < 0) return setError("El costo no puede ser negativo");
    if (!tecnico.trim()) return setError("Indique el técnico");
    onRegistrar({
      garantiaId: garantia.id,
      numero: numero === EXTRA ? null : Number(numero),
      fecha,
      km: kmNum,
      tecnico: tecnico.trim(),
      costo: costoNum,
      observaciones: observaciones.trim(),
    });
    return null;
  };

  if (!garantia) return null;

  return (
    <Dialog
      open={abierto}
      onClose={guardando ? undefined : onCerrar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, boxShadow: FP.shadow.md } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 0.5 }}>
        Registrar servicio
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
          {garantia.chasis} · {garantia.marca} {garantia.modelo} · {garantia.cliente}
        </Typography>
        <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Servicio"
              value={numero}
              onChange={(e) => cambiarServicio(e.target.value)}
              sx={fpInputSx}
            >
              {pendientes.map((s) => (
                <MenuItem key={s.numero} value={String(s.numero)}>
                  {s.numero} · {s.nombre} — {ESTADOS_SERVICIO[s.estado].label.toLowerCase()} (
                  {fmtFecha(s.desde)} – {fmtFecha(s.hasta)}) · {fmtPrecio(s.precio)}
                </MenuItem>
              ))}
              <MenuItem value={EXTRA}>Servicio extra (fuera del plan)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Fecha"
              value={fecha}
              onChange={(e) => {
                setFecha(e.target.value);
                setError("");
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: hoy, min: garantia.fechaVenta }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Km (informativo)"
              value={km}
              onChange={(e) => {
                setKm(e.target.value.replace(/[^\d]/g, ""));
                setError("");
              }}
              inputProps={{ inputMode: "numeric" }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Técnico"
              value={tecnico}
              onChange={(e) => {
                setTecnico(e.target.value);
                setError("");
              }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Costo RD$"
              value={costo}
              onChange={(e) => {
                setCosto(e.target.value.replace(/[^\d.]/g, ""));
                setError("");
              }}
              inputProps={{ inputMode: "decimal" }}
              helperText={Number(costo) === 0 ? "Servicio gratis" : " "}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              sx={fpMultilineInputSx}
            />
          </Grid>
        </Grid>
        {error ? (
          <Typography sx={{ color: FP.color.danger, fontSize: "0.85rem", fontWeight: 600, mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCerrar} sx={{ ...btnBase, color: FP.color.textSecondary }}>
          Cancelar
        </Button>
        <Button onClick={guardar} disabled={guardando} sx={btnSolid()}>
          {guardando ? "Registrando…" : "Registrar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
